import React, { createContext, useEffect, useState, useRef, useMemo } from "react";
import * as signalR from "@microsoft/signalr";
import CONFIG from "../config";

export const RadioContext = createContext();

export function RadioProvider({ children }) {
  const [nowPlaying, setNowPlaying] = useState({
    title: "Loading...",
    listeners: 0,
    coverUrl: "/images/default-cover-1.png",
    frameUrl: "/images/default-cover-1.png",
    duration: "",
  });
  const [history, setHistory] = useState([]);

  const lastNowPlayingRef = useRef(null);
  const lastHistoryRef = useRef(null);
  const hubUrl = CONFIG.hubUrl;

  useEffect(() => {
    let isMounted = true;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect()
      .build();

    const handleNowPlaying = (data) => {
      if (!isMounted) return;
      const formattedData = {
        ...data,
        currentLiveTime: data.currentLiveTime ? new Date(data.currentLiveTime) : null,
      };

      if (JSON.stringify(formattedData) !== JSON.stringify(lastNowPlayingRef.current)) {
        lastNowPlayingRef.current = formattedData;
        setNowPlaying((prev) => ({ ...prev, ...formattedData }));
      }

      if (
        data.history &&
        JSON.stringify(data.history) !== JSON.stringify(lastHistoryRef.current)
      ) {
        lastHistoryRef.current = data.history;
        setHistory(data.history);
      }
    };

    let lastUpdate = 0;
    const handleTimeStatus = (data) => {
      if (!isMounted) return;
      const now = Date.now();
      if (now - lastUpdate < 500) return;
      lastUpdate = now;

      setNowPlaying((prev) => ({
        ...prev,
        currentLiveTime: new Date(data.currentLiveTime),
        playbackTime: new Date(data.playbackTime),
        maxTimeShiftSeconds: data.maxTimeShiftSeconds,
        listeners: data.listeners,
      }));
    };

    connection.on("ReceiveNowPlaying", handleNowPlaying);
    connection.on("ReceiveTimeStatus", handleTimeStatus);

    connection
      .start()
      .then(() => {
        if (!isMounted) connection.stop();
      })
      .catch((err) => {
        if (err?.message?.includes("stopped during negotiation")) return;
        console.warn("SignalR connection error:", err.message || err);
      });

    return () => {
      isMounted = false;
      connection.off("ReceiveNowPlaying", handleNowPlaying);
      connection.off("ReceiveTimeStatus", handleTimeStatus);
      connection.stop().catch(() => { });
    };
  }, [hubUrl]);


  const contextValue = useMemo(
    () => ({ nowPlaying, history }),
    [nowPlaying, history]
  );

  return (
    <RadioContext.Provider value={contextValue}>
      {children}
    </RadioContext.Provider>
  );
}
